import { useEffect, useState, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useApi } from '@/services/api';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { Text } from '@/components/ui/text';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { formatDistanceToNow } from 'date-fns';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useNotificationsSSE } from '@/hooks/useNotificationsSSE';
import { useAuth } from '@/context/AuthContext';

const POLL_INTERVAL_MS = 30000; // 30s

const NotificationsBell = () => {
  const [count, setCount] = useState<number>(0);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { notifications } = useApi();
  const navigate = useNavigate();
  const mounted = useRef(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [markingAllError, setMarkingAllError] = useState<string | null>(null);
  const { accessToken, isAuthenticated } = useAuth();
  const fetchingRef = useRef(false); // Prevent concurrent requests
  const lastFetchRef = useRef(0); // Debounce fetch requests

  const fetchCount = useCallback(async () => {
    if (!isAuthenticated || !accessToken || accessToken === 'null' || accessToken === 'undefined' || fetchingRef.current) {
      setCount(0);
      return;
    }

    // Debounce: Don't fetch if we just fetched within the last 2 seconds
    const now = Date.now();
    if (now - lastFetchRef.current < 2000) {
      return;
    }

    fetchingRef.current = true;
    lastFetchRef.current = now;

    try {
      const res = await notifications.getUnreadCount();
      if (typeof res === 'number') setCount(res);
      else if (res && typeof res === 'object') setCount((res as any).count || 0);
      else setCount(0);
    } catch (e: any) {
      // Only log error if it's not a 401 (unauthorized) error
      if (!e.message?.includes('401') && !e.message?.includes('Unauthorized')) {
        console.error('Failed to fetch unread notifications count', e);
      }
      // Set count to 0 to prevent UI issues when notifications service is down
      setCount(0);
    } finally {
      fetchingRef.current = false;
    }
  }, [isAuthenticated, accessToken, notifications]);

  const fetchList = useCallback(async () => {
    if (!isAuthenticated || !accessToken || accessToken === 'null' || accessToken === 'undefined') {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const resp = await notifications.getNotifications({ page: 0, size: 5 });
      const content = resp?.content || resp || [];
      setItems(content);
    } catch (e) {
      console.error('Failed to fetch notifications list', e);
      // Set empty list to prevent UI issues when notifications service is down
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accessToken, notifications]);

  useEffect(() => {
    mounted.current = true;
    fetchCount();

    const interval = setInterval(() => {
      fetchCount();
      // only refresh list when dropdown is open to avoid unnecessary work
      if (open) fetchList();
    }, POLL_INTERVAL_MS);

    return () => {
      mounted.current = false;
      clearInterval(interval);
    };
  }, [open, fetchCount, fetchList]);

  // When opening the dropdown, fetch recent notifications
  useEffect(() => {
    if (open && isAuthenticated) fetchList();
  }, [open, isAuthenticated]);

  useNotificationsSSE((payload) => {
    // Prepend the new notification
    if (!isAuthenticated) return;
    setItems((prev) => [payload, ...prev].slice(0, 50));
    setCount((c) => c + 1);
    toast({ title: 'New notification', description: payload.message || payload.title });
  });

  const handleMarkAsRead = async (id: string) => {
    // optimistic update: mark locally first
    const prev = items;
    setItems(items.map((n) => (n.id === id ? { ...n, status: 'READ' } : n)));
    setCount((c) => Math.max(0, c - 1));

    try {
      await notifications.markAsRead(id);
      // success - nothing else to do
    } catch (e) {
      // revert optimistic update
      setItems(prev);
      await fetchCount();
      toast({ title: 'Failed to mark notification', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleMarkAllConfirmed = async () => {
    setIsMarkingAll(true);
    setMarkingAllError(null);
    try {
      const res = await notifications.markAllAsRead();
      // res expected to be array of ids
      const markedIds: string[] = Array.isArray(res) ? res : [];
      // Update local state for those marked ids
      setItems((prev) => prev.map((n) => (markedIds.includes(n.id) ? { ...n, status: 'READ' } : n)));
      setCount(0);

      // Show undo toast with action
      toast({
        title: 'All notifications marked as read',
        description: 'You can undo this action',
        action: (
          <ToastAction altText="Undo" onClick={async () => {
            try {
              await notifications.undoMarkRead(markedIds);
              // revert UI
              setItems((prev) => prev.map((n) => (markedIds.includes(n.id) ? { ...n, status: 'SENT' } : n)));
              // refresh unread count
              const cnt = await notifications.getUnreadCount();
              setCount(typeof cnt === 'number' ? cnt : (cnt as any).count || 0);
              toast({ title: 'Undo successful' });
            } catch (e) {
              toast({ title: 'Undo failed', variant: 'destructive' });
            }
          }}>
            Undo
          </ToastAction>
        ),
      });
      setConfirmOpen(false);
    } catch (e: any) {
      console.error('Failed to mark all as read', e);
      setMarkingAllError(e.message || 'Failed to mark all notifications as read. Please try again.');
    } finally {
      setIsMarkingAll(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative" aria-label="Notifications">
          <Bell />
          {isAuthenticated && count > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs rounded-full bg-red-600 text-white">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between px-2">
            <Text className="font-semibold">Notifications</Text>
            {isAuthenticated && (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(true)}>Mark all</Button>
                <Button variant="ghost" size="sm" onClick={() => { navigate('/notifications'); setOpen(false); }}>
                  View all
                </Button>
              </div>
            )}
          </div>

          {!isAuthenticated ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Sign in to view notifications</div>
          ) : loading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No new notifications</div>
          ) : (
            <div className="max-h-64 overflow-auto space-y-2">
              {items.map((n) => (
                <div key={n.id} className={`p-2 rounded-md flex items-start gap-2 ${n.status === 'READ' ? 'bg-white' : 'bg-muted'}`}>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{n.subject || n.title || n.message || 'Notification'}</div>
                    <div className="text-xs text-muted-foreground">{n.message}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : ''}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {n.status !== 'READ' && (
                      <Button size="sm" onClick={() => handleMarkAsRead(n.id)}>Mark</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>

      <Dialog open={confirmOpen} onOpenChange={(open) => {
        setConfirmOpen(open);
        if (!open) {
          setMarkingAllError(null);
          setIsMarkingAll(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark all notifications as read?</DialogTitle>
          </DialogHeader>
          <div className="py-2">This will mark all recent notifications as read. This action cannot be undone.</div>
          {markingAllError && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/50 p-2.5 rounded-md border border-red-200 dark:border-red-900 my-2">
              {markingAllError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isMarkingAll}>Cancel</Button>
            <Button onClick={handleMarkAllConfirmed} disabled={isMarkingAll}>
              {isMarkingAll ? 'Confirming...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DropdownMenu>
  );
};

export default NotificationsBell;

